/**
 * ToolGuard — a route-level gate that respects the PostHog
 * `tool_killswitch` flag.
 *
 * Wraps the page element in App.tsx route declarations so disabled
 * tools never mount the underlying page component at all (no wasted
 * /history fetches, no Rules-of-Hooks footguns inside the page).
 *
 *   <Route path="/dashboard/manitoba" element={
 *     <ProtectedRoute>
 *       <ToolGuard tool="manitoba" name="Manitoba Learning Behaviours">
 *         <ManitobaPage />
 *       </ToolGuard>
 *     </ProtectedRoute>
 *   } />
 *
 * Why a gate component rather than a hook called inside the page:
 * pages are 200-500 lines with dozens of hook calls. Early-returning
 * from inside the page based on the killswitch would change the
 * number/order of hook calls when the flag flipped — illegal under
 * React's Rules of Hooks. Gating at the route boundary sidesteps the
 * problem entirely: when killed, the wrapped page simply isn't
 * mounted, so no `useEffect` / `useQuery` calls inside it ever fire.
 *
 * The `tool_killswitch` flag is multivariate. Default variant `none`
 * means everything works; flipping to a specific tool id ("rubric",
 * "manitoba", etc.) disables ONLY that tool org-wide. The other tools
 * keep running.
 */
import React from "react";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import ToolUnavailableBanner from "./ToolUnavailableBanner";

export type ToolId =
  | "learning_skills"
  | "kindergarten"
  | "manitoba"
  | "bc_subject"
  | "ontario_subject"
  | "rubric"
  | "worksheets";

interface ToolGuardProps {
  tool: ToolId;
  name: string;
  children: React.ReactNode;
}

export default function ToolGuard({ tool, name, children }: ToolGuardProps) {
  const flag = useFeatureFlag<string>("tool_killswitch", "none");
  if (flag === tool) {
    return <ToolUnavailableBanner toolName={name} />;
  }
  return <>{children}</>;
}
